import { PURPOSE_OPTIONS } from "../../constants/vehiclePurposes.ts";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import CalculatorService from "../../services/CalculatorService/CalculatorService.ts";
import { INSURERS } from "../../constants/insurers.ts";
import type {
  AggregatorResponse,
  AggregatorHolder,
} from "../../types/calculatorResponse.ts";
import type {
  CalcNavigationState,
  CalcVehicle,
  CalcOwner,
} from "../../types/calculatorTypes.ts";

// ─── Types ────────────────────────────────────────────────────────────────────

type PremiumTotals = Record<string, number>;

// ─── Provider metadata ────────────────────────────────────────────────────────

const PROVIDERS: {
  key: string;
  label: string;
  shortLabel: string;
  color: string;
  bg: string;
  route?: string;
}[] = [
  {
    key: "dzi",
    label: "ДЗИ",
    shortLabel: "ДЗИ",
    color: "#005baa",
    bg: "#e8f0fb",
    route: "/integrations/dzi",
  },
  {
    key: "ozk",
    label: "ОЗК",
    shortLabel: "ОЗК",
    color: "#e5222a",
    bg: "#fdeaea",
    route: "/integrations/ozk",
  },
  {
    key: "generali",
    label: "Дженерали",
    shortLabel: "ДЖ",
    color: "#cc0000",
    bg: "#fdeaea",
    route: "/integrations/generali",
  },
  {
    key: "levIns",
    label: "Лев Инс",
    shortLabel: "ЛИ",
    color: "#2b6cb0",
    bg: "#ebf4ff",
    route: "/integrations/levins",
  },
];

// ─── InfoRow ──────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-[11px] text-body dark:text-bodydark">{label}</span>
    <span className="text-xs font-semibold text-black dark:text-white">
      {value ?? "—"}
    </span>
  </div>
);

// ─── PremiumCard ──────────────────────────────────────────────────────────────

const PremiumCard: React.FC<{
  providerKey: string;
  amount: number;
  isLowest: boolean;
  vehicle: CalcVehicle | null;
  owner: CalcOwner | null;
  holder: AggregatorHolder | null;
}> = ({ providerKey, amount, isLowest, vehicle, owner, holder }) => {
  const navigate = useNavigate();
  const meta = PROVIDERS.find((p) => p.key === providerKey);
  const label = meta?.label ?? providerKey;
  const shortLabel = meta?.shortLabel ?? providerKey.slice(0, 2).toUpperCase();
  // Тази страница ползва camelCase ключове ("dallBogg", "levIns"), а справочникът —
  // идентификаторите на шлюза ("dallbogg", "levins").
  const logo = INSURERS[providerKey.toLowerCase()]?.logo;
  const color = meta?.color ?? "#243095";
  const bg = meta?.bg ?? "#eef0fb";

  const route = meta?.route;

  const handleNavigate = () => {
    if (!route) return;
    const state: CalcNavigationState | undefined =
      vehicle && owner
        ? { vehicle, owner, holder, totalPremium: amount }
        : undefined;
    navigate(route, { state });
  };

  return (
    <div
      className={`group relative flex items-center gap-3 rounded-sm border bg-white px-3 py-2.5 transition-shadow duration-150 hover:shadow-md dark:bg-boxdark ${
        isLowest
          ? "border-[#243095] ring-1 ring-[#243095]/20"
          : "border-stroke dark:border-strokedark"
      }`}
    >
      {/* Логото на застрахователя, с инициалите като резервен вариант — оцветеният
          кръг остава фонът, за да не се променя оформлението на картата. */}
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-[11px] font-bold"
        style={{ background: bg, color }}
      >
        {logo ? (
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            className="h-6 w-6 object-contain"
          />
        ) : (
          shortLabel
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-black dark:text-white">
            {label}
          </span>
          {isLowest && (
            <span className="rounded bg-[#243095] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
              Най-ниска
            </span>
          )}
        </div>
        <span className="text-[11px] text-body dark:text-bodydark">
          Гражданска отговорност
        </span>
      </div>

      <div className="mr-8 text-right">
        <span className="text-base font-bold" style={{ color }}>
          {amount.toFixed(2)}
        </span>
        <span className="ml-1 text-xs text-body dark:text-bodydark">EUR</span>
      </div>

      {route && (
        <button
          onClick={handleNavigate}
          className="absolute right-2.5 flex h-7 w-7 items-center justify-center rounded-full text-[#3C50E0] opacity-0 transition-all duration-150 hover:bg-[#3C50E0]/10 group-hover:opacity-100"
          title={`Към интеграция — ${label}`}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const InsuranceCalculator: React.FC = () => {
  const { jwt } = useAuth();
  const [plate, setPlate] = useState("");
  const [certNumber, setCertNumber] = useState("");
  // Policyholder ЕГН/ЕИК and vehicle purpose. Left blank, the backend resolves them
  // from the vehicle data (owner + registered usage) and echoes them in `resolved`.
  const [pin, setPin] = useState("");
  const [purpose, setPurpose] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [vehicle, setVehicle] = useState<CalcVehicle | null>(null);
  const [owner, setOwner] = useState<CalcOwner | null>(null);
  const [holder, setHolder] = useState<AggregatorHolder | null>(null);
  const [premiums, setPremiums] = useState<PremiumTotals | null>(null);

  const prevPlateRef = useRef("");

  const CYRILLIC_TO_LATIN_PLATE: Record<string, string> = {
    А: "A",
    В: "B",
    Е: "E",
    К: "K",
    М: "M",
    Н: "H",
    О: "O",
    Р: "P",
    С: "C",
    Т: "T",
    У: "Y",
    Х: "X",
  };
  const transliteratePlate = (plate: string): string =>
    plate
      .split("")
      .map((ch) => CYRILLIC_TO_LATIN_PLATE[ch] ?? ch)
      .join("");

  // ── Lookup ────────────────────────────────────────────────────────────────

  const lookup = async (regPlate: string) => {
    const normalized = regPlate.trim().toUpperCase();
    if (!normalized || normalized === prevPlateRef.current) return;
    prevPlateRef.current = normalized;

    setLoading(true);
    setError(null);
    setVehicle(null);
    setOwner(null);
    setHolder(null);
    setPremiums(null);

    try {
      const data: AggregatorResponse =
        await CalculatorService.calculateAggregatedPremium(jwt, {
          registrationPlate: transliteratePlate(normalized),
          certificateNumber: certNumber.trim(),
          pin: pin.trim(),
          ekatte: "",
          purpose: purpose,
          experience: 0,
          accidents: false,
          maxWeightF1: 0,
          include: {
            dziBM: true,
            dziPremium: true,
            ozkBM: true,
            ozkPremium: true,
            // DallBogg е изключен от котирането. Задължително false: бекендът
            // слива частичния include върху подразбиране "всичко включено", така че
            // само премахването на ключа би го оставило активен. Изключеният BM
            // маха и DallBogg от веригата за попълване на данни за МПС.
            dallBoggBM: false,
            dallBoggPremium: false,
            generaliPremium: true,
            levInsVehicleData: true,
            levInsPremium: true,
            // Temporarily disabled on both sides; keep this explicit so a backend
            // default change cannot make Armeec reappear in the unified UI.
            armeecPremium: false,
          },
          // No `stubs`: the backend defaults all providers to LIVE, so real
          // quotes are returned (no canned stub premiums).
        });

      // ── Vehicle ──────────────────────────────────────────────────────────
      // The backend (aggregator) now consolidates vehicle fields across all
      // providers (DZI → OZK → L-Ins, in priority order) and
      // returns them directly on `data.vehicle` (e.g. `data.vehicle.make`).
      const gf = data?.vehicle?.dzi?.data;
      const v = data?.vehicle;

      const vehicleState: CalcVehicle = {
        regNo: gf?.gfRegNo ?? normalized,
        certNumber: certNumber.trim(),
        chassis: v?.chassis ?? "",
        make: v?.make ?? "",
        model: v?.model ?? "",
        fuelType: v?.fuelType ?? "",
        seats: v?.seats ?? 0,
        enginePower: v?.enginePower ?? 0,
        displacement: v?.displacement ?? 0,
        productionYear: v?.productionYear ?? 0,
        age: v?.age ?? 0,
        firstRegistrationDate: v?.firstRegistrationDate ?? "",
        maxWeightF1: v?.maxWeightF1 ?? undefined,
      };
      setVehicle(vehicleState);

      // Normalized, issue-ready policyholder (per-provider codes, EKATTE, name parts).
      setHolder(data.holder ?? null);

      // ── Owner ─────────────────────────────────────────────────────────────
      const gfOwners = gf?.gfOwners;
      if (gfOwners?.length) {
        const first = gfOwners[0];
        const lastAddr = first.addresses?.[first.addresses.length - 1];
        setOwner({
          name: first.name ?? "",
          pid: first.pid ?? "",
          personType: first.personType ?? "1",
          address: lastAddr?.address ?? "",
          ekatte: lastAddr?.cityCode ?? "",
          isLeasing: first.isLeasing === "Y",
        });
      } else if (data?.owners?.length) {
        const first = data.owners[0];
        const lastAddr = first.addresses?.[first.addresses.length - 1];
        setOwner({
          name: first.name ?? "",
          pid: first.id ?? "",
          personType: first.type ?? "1",
          address: lastAddr?.line ?? "",
          ekatte: lastAddr?.ekatte ?? "",
          isLeasing: first.isLeasing === "Y",
        });
      }

      // Reflect what the backend actually used (owner ЕГН + inferred purpose) so
      // the user sees it and can override, then re-search.
      if (data?.resolved) {
        if (data.resolved.pin) setPin(data.resolved.pin);
        if (data.resolved.purpose) setPurpose(data.resolved.purpose);
      }

      // ── Premiums ──────────────────────────────────────────────────────────
      // Normalize backend keys to match PROVIDERS keys
      const normalizeProviderKey = (k: string): string => {
        if (k === "levins") return "levIns";
        return k;
      };

      // Extract a numeric amount from either a raw number or a quote object
      const extractAmount = (val: unknown): number | null => {
        if (typeof val === "number" && val > 0) return val;
        if (typeof val === "object" && val !== null) {
          const v = val as any;
          if (typeof v.totalDue === "number" && v.totalDue > 0)
            return v.totalDue;
          if (typeof v.grossPremium === "number" && v.grossPremium > 0)
            return v.grossPremium;
          if (typeof v.total === "number" && v.total > 0) return v.total;
        }
        return null;
      };

      const norm: PremiumTotals = {};

      // 1. Read from premiums.totals
      const totals = data?.premiums?.totals;
      if (totals && typeof totals === "object") {
        for (const [k, val] of Object.entries(totals)) {
          if (normalizeProviderKey(k) === "armeec") continue;
          const amount = extractAmount(val);
          if (amount !== null) norm[normalizeProviderKey(k)] = amount;
        }
      }

      // 2. Read per-provider entries directly from premiums (dzi, ozk, generali...)
      for (const [k, val] of Object.entries(data?.premiums ?? {})) {
        if (k === "totals") continue;
        const nk = normalizeProviderKey(k);
        if (nk === "armeec") continue;
        if (nk in norm) continue; // already have from totals
        const amount = extractAmount(val);
        if (amount !== null) norm[nk] = amount;
      }

      if (Object.keys(norm).length > 0) {
        setPremiums(norm);
      }
    } catch (err: any) {
      setError(err.message ?? "Неочаквана грешка");
    } finally {
      setLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const sortedPremiums = premiums
    ? Object.entries(premiums).sort(([, a], [, b]) => a - b)
    : [];
  const lowestKey = sortedPremiums[0]?.[0] ?? null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      {/* ── Search ── */}
      <fieldset className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <SectionHeader title="Калкулатор — Гражданска отговорност" />
        <div className="p-4 pt-2">
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Регистрационен номер</label>
              <input
                type="text"
                value={plate}
                onChange={(e) => {
                  setPlate(e.target.value.toUpperCase());
                  // New vehicle → reset the auto-resolved pin/purpose so they don't
                  // leak the previous owner/usage into the next lookup.
                  setPin("");
                  setPurpose("");
                }}
                onKeyDown={(e) => e.key === "Enter" && lookup(plate)}
                placeholder="CA1458BT"
                className={`${inputClass} uppercase tracking-widest`}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>№ на свидетелство</label>
              <input
                type="text"
                value={certNumber}
                onChange={(e) => setCertNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && lookup(plate)}
                placeholder="000124799"
                className={inputClass}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>
                ЕГН / ЕИК{" "}
                <span className="text-[10px] text-body dark:text-bodydark">
                  (по избор)
                </span>
              </label>
              <input
                type="text"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && lookup(plate)}
                placeholder="напр. 9208047184"
                className={inputClass}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>
                Предназначение{" "}
                <span className="text-[10px] text-body dark:text-bodydark">
                  (по избор)
                </span>
              </label>
              <SelectGroup
                value={purpose}
                onChange={(v) => setPurpose(v)}
                options={PURPOSE_OPTIONS}
                placeholder="— автоматично —"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-body dark:text-bodydark">
              Въведете данните и натиснете Enter или Търси
            </p>
            <button
              onClick={() => {
                prevPlateRef.current = "";
                lookup(plate);
              }}
              disabled={loading || !plate.trim() || !certNumber.trim()}
              className="flex shrink-0 items-center gap-1.5 rounded bg-[#243095] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:bg-opacity-90 disabled:opacity-50"
            >
              {loading ? (
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                  />
                </svg>
              )}
              {loading ? "Търсене..." : "Търси"}
            </button>
          </div>
        </div>
      </fieldset>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-2 rounded-sm border border-[#AA2808]/30 bg-red-50 p-3 dark:bg-red-900/10">
          <svg
            className="mt-0.5 h-4 w-4 shrink-0 text-[#AA2808]"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-[#AA2808]">{error}</p>
        </div>
      )}

      {/* ── Results ── */}
      {(vehicle || owner) && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {vehicle && (
              <fieldset className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                <SectionHeader title="Данни за МПС" />
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 pt-2">
                  <InfoRow label="Рег. номер" value={vehicle.regNo} />
                  <InfoRow label="Шаси" value={vehicle.chassis} />
                  <InfoRow
                    label="Марка / Модел"
                    value={`${vehicle.make} ${vehicle.model}`.trim() || "—"}
                  />
                  <InfoRow label="Гориво" value={vehicle.fuelType} />
                  <InfoRow
                    label="Куб. см"
                    value={vehicle.displacement || "—"}
                  />
                  <InfoRow
                    label="Мощност (к.с.)"
                    value={vehicle.enginePower || "—"}
                  />
                  <InfoRow label="Места" value={vehicle.seats || "—"} />
                  <InfoRow
                    label="Год. производство"
                    value={vehicle.productionYear || "—"}
                  />
                  <InfoRow label="Възраст (год.)" value={vehicle.age || "—"} />
                  <InfoRow
                    label="1-ва регистрация"
                    value={
                      vehicle.firstRegistrationDate
                        ? new Date(
                            vehicle.firstRegistrationDate
                          ).toLocaleDateString("bg-BG")
                        : "—"
                    }
                  />
                </div>
              </fieldset>
            )}

            {owner && (
              <fieldset className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                <SectionHeader title="Данни за клиент" />
                <div className="grid grid-cols-1 gap-y-2 p-3 pt-2">
                  <div className="mb-1 flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#243095]/10 text-sm font-bold text-[#243095]">
                      {owner.name
                        .split(" ")
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-black dark:text-white">
                        {owner.name}
                      </p>
                      <p className="text-[11px] text-body dark:text-bodydark">
                        {owner.personType === "1"
                          ? "Физическо лице"
                          : "Юридическо лице"}
                      </p>
                    </div>
                  </div>
                  <InfoRow
                    label={owner.personType === "1" ? "ЕГН" : "ЕИК"}
                    value={owner.pid}
                  />
                  <InfoRow
                    label="Лизинг"
                    value={owner.isLeasing ? "Да" : "Не"}
                  />
                  {owner.address && (
                    <InfoRow label="Адрес" value={owner.address} />
                  )}
                </div>
              </fieldset>
            )}
          </div>

          {sortedPremiums.length > 0 && (
            <fieldset className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
              <SectionHeader title="Оферти от застрахователи" />
              <div className="space-y-2 p-3 pt-2">
                {sortedPremiums.map(([key, amount]) => (
                  <PremiumCard
                    key={key}
                    providerKey={key}
                    amount={amount}
                    isLowest={key === lowestKey}
                    vehicle={vehicle}
                    owner={owner}
                    holder={holder}
                  />
                ))}
              </div>
            </fieldset>
          )}
        </>
      )}
    </div>
  );
};

export default InsuranceCalculator;
